import React, { useMemo } from 'react';
import { filter, findIndex } from 'lodash';
import PropTypes from 'prop-types';
import { Controller, useForm } from 'react-hook-form';
import {
  Box,
  Button,
  CardContent,
  FilePdfIcon,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  Link,
  Paper,
  Radio,
  RadioGroup,
  Typography,
} from '@availity/element';

import { AvailityHeaderLogo, Footer } from '@/components';
import { useFRCallback } from '@/hooks';
import { TwoFAIcon01 } from '@/static';

const radioLabels = {
  totp: 'Use an authenticator app, such as Google Authenticator, to generate my code.',
  sms: 'Text me my authentication code.',
  voice: 'Call me with my authentication code.',
  token: 'My organization requires the use of a hard token device.',
};

const faqLink = 'https://www.availity.com/documents/learning/QRG_AP_2step_Providers.pdf';

const TwoFAEnrollmentSelectMethod = ({ ...props }) => {
  const { stepToRender, setCurrentStep } = props;

  const { callbacks, choiceCallbackChoices, choiceCallbackIndex } = useFRCallback(stepToRender);

  // need to extract the token option from the radio group
  const choiceRadioOptions = useMemo(() => {
    const optionsToReturn = filter(choiceCallbackChoices, (choice) => choice?.toLowerCase() !== 'token');

    return optionsToReturn;
  }, [choiceCallbackChoices]);

  // build form handler
  const {
    handleSubmit,
    formState: { errors },
    control,
  } = useForm({
    defaultValues: {
      choice: '',
    },
  });

  const onSubmit = (values) => {
    const { choice = '' } = values;

    const nextStep = stepToRender;
    const newCallbacks = [...callbacks];

    // get index of the selected option
    const indexToUse = choiceCallbackChoices.indexOf(choice);

    // set the choice callback input based off the index of the selected option in the callback
    newCallbacks[choiceCallbackIndex].input[0] = { ...newCallbacks[choiceCallbackIndex].input[0], value: indexToUse };

    // set the newCallbacks in the newStep
    Object.assign(nextStep.payload.callbacks, newCallbacks);

    // update the currentStep to trigger navigation
    setCurrentStep(nextStep);
  };

  const handleNavigation = (selected) => {
    const nextStep = stepToRender;

    if (selected === 'token') {
      const newCallbacks = [...callbacks];

      // get index of the choice callback
      const choiceCallbackIndex = findIndex(
        nextStep?.payload?.callbacks,
        (callbackObj) => callbackObj?.type?.toLowerCase() === 'choicecallback'
      );

      // get index of the selected option
      const indexToUse = choiceCallbackChoices.indexOf('token');

      // set the choice callback input based off the index of the selected option in the callback
      newCallbacks[choiceCallbackIndex].input[0] = { ...newCallbacks[choiceCallbackIndex].input[0], value: indexToUse };

      // set the newCallbacks in the newStep
      Object.assign(nextStep.payload.callbacks, newCallbacks);

      // update the currentStep to trigger navigation
      setCurrentStep(nextStep);
    }
  };

  const radioOptions = useMemo(() => {
    const output = [];
    for (const choice of choiceRadioOptions) {
      output.push(
        <FormControlLabel
          key={`elect-${choice}-radio`}
          value={choice}
          control={<Radio />}
          label={radioLabels[choice]}
        />
      );
    }

    return output;
  }, [choiceRadioOptions]);

  return (
    <Grid
      id="2fa-enrollment-select-method-page-grid-container"
      container
      display="flex"
      flexDirection="row"
      flexWrap="wrap"
      spacing={0}
      sx={{ minHeight: '100vh', justifyContent: 'center' }}
    >
      <Grid
        id="2fa-enrollment-page-content-grid"
        container
        display="flex"
        flexDirection="column"
        alignItems="center"
        flexGrow={1}
        mt="auto"
      >
        <AvailityHeaderLogo />
        <Grid direction="column" alignItems="center" justifyContent="center">
          <Paper
            variant="elevation"
            elevation={6}
            sx={{ width: { xs: '350px', sm: '500px', md: '750px', lg: '1000px' } }}
          >
            <form onSubmit={handleSubmit(onSubmit)}>
              <CardContent>
                <Grid display="flex" flexDirection="row" flexWrap="wrap">
                  <Grid size={{ sm: 12, md: 4 }} sx={{ marginBottom: { xs: 2, md: 0 }, paddingRight: 2 }}>
                    <Box textAlign="center" mb={2}>
                      <img
                        src={TwoFAIcon01}
                        alt="2FA Icon"
                        style={{
                          maxWidth: '45%',
                        }}
                      />
                    </Box>
                    <Typography variant="h5" component="h3" mb={2}>
                      What is 2-step authentication?
                    </Typography>
                    <Typography variant="subtitle1">
                      2-step authentication protects your account by asking you for two pieces of information: your
                      username & password combination plus a six digit code that will be sent to you.
                    </Typography>
                    <Box mt={3}>
                      <Link
                        href={faqLink}
                        target="_blank"
                        loadApp={false}
                        rel="noopener noreferrer"
                        aria-label="Get answers to common questions (opens PDF in a new window)"
                      >
                        Answers to common questions <FilePdfIcon fontSize="small" />
                      </Link>
                    </Box>
                  </Grid>
                  <Grid
                    display="flex"
                    flexDirection="column"
                    size={{ md: 8 }}
                    sx={{ borderLeft: { sm: 0, md: 1 }, borderColor: { md: '#D7DAE0' } }}
                  >
                    <Box pl={2}>
                      <Typography variant="h1" mb={1}>
                        2-Step Authentication
                      </Typography>
                      <Typography variant="h4" component="h2" mb={2}>
                        Set up your 2-Step Authentication
                      </Typography>
                    </Box>
                    <Grid display="flex" flexDirection="column" flexGrow={1} pl={2}>
                      <Box mb={2} mt={2}>
                        <FormControl error={!!errors.choice}>
                          <FormLabel component="legend" id="2fa-options-radio-group-label" sx={{ marginBottom: 1 }}>
                            <Typography sx={{ fontWeight: 'bold' }}>
                              How would you like to receive your code?
                            </Typography>
                          </FormLabel>
                          <Controller
                            control={control}
                            name="choice"
                            rules={{ required: 'Select an option.' }}
                            render={({ field }) => (
                              <RadioGroup
                                aria-labelledby="2fa-options-radio-group-label"
                                name="choice"
                                {...field}
                                aria-required
                              >
                                {radioOptions}
                              </RadioGroup>
                            )}
                          />
                          <FormHelperText id="2fa-options-radio-group-helpertext">
                            {errors.choice?.message}
                          </FormHelperText>
                        </FormControl>
                      </Box>
                      <Box mb={5}>
                        <Button color="tertiary" size="small" onClick={() => handleNavigation('token')}>
                          {radioLabels.token}
                        </Button>
                      </Box>
                      <Box textAlign="right" mt="auto">
                        <Button color="primary" sx={{ marginLeft: 1 }} type="submit">
                          Continue
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>
              </CardContent>
            </form>
          </Paper>
        </Grid>
      </Grid>
      <Footer />
    </Grid>
  );
};

TwoFAEnrollmentSelectMethod.propTypes = {
  stepToRender: PropTypes.object.isRequired,
  setCurrentStep: PropTypes.func.isRequired,
};

export default TwoFAEnrollmentSelectMethod;
